import { Component, OnInit } from '@angular/core';
import {
  DailyMovementLimitResult,
  DailyMovementLimitService,
} from '../../../services/daily-movement-limit.service';
import { CustomerService, UserService } from '../../../services';
import { OrderLimitTypeEnum } from '../../../enums/order-limit-type.enum';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { DayColor } from '../../../modules/driver-experience/app-full-calendar/app-full-calendar.component';
import { EventInput, EventSourceInput } from '@fullcalendar/core';

@Component({
  templateUrl: './order-limits-admin.component.html',
  styleUrls: ['./order-limits-admin.component.scss'],
})
export class OrderLimitsAdminComponent implements OnInit {
  constructor(
    private dailyLimitsService: DailyMovementLimitService,
    private customerService: CustomerService,
    private fb: FormBuilder,
    private userService: UserService,
  ) {}

  public OrderLimitType = OrderLimitTypeEnum;

  public movementLimits: DailyMovementLimitResult[];

  public drivenLimits: EventSourceInput;
  public transportLimits: EventSourceInput;

  public drivenDayColors: DayColor[];
  public transportDayColors: DayColor[];

  currentCustomerId = null;

  selectedCustomer: any;
  customers: any;
  customersLoaded = false;

  editDayLimits = false;
  selectedDate: string;

  form: FormGroup;
  customerSearchForm: FormGroup;
  userIsPOAD = false;

  async ngOnInit() {
    this.userIsPOAD = await this.userService.isUserInRole('POAD');

    this.form = this.fb.group({
      date: ['', Validators.required],
      drivenLimit: [],
      transportedLimit: [],
      customerDrivenLimit: [],
      customerTransportedLimit: [],
      customerId: [],
    });

    this.customerSearchForm = this.fb.group({
      name: [''],
    });

    // load limits and totals
    this.dailyLimitsService.getDailyMovementLimits().then((res) => {
      this.movementLimits = res;
      this.resetLimits();
    });
  }

  resetLimits() {
    this.drivenLimits = this.processMovementLimits(this.movementLimits, OrderLimitTypeEnum.Driven);

    this.drivenDayColors = this.dailyLimitsService.getDayColors(
      this.movementLimits,
      OrderLimitTypeEnum.Driven,
      this.selectedCustomer,
    );

    this.transportLimits = this.processMovementLimits(
      this.movementLimits,
      OrderLimitTypeEnum.Transported,
    );

    this.transportDayColors = this.dailyLimitsService.getDayColors(
      this.movementLimits,
      OrderLimitTypeEnum.Transported,
      this.selectedCustomer,
    );
  }

  searchCustomers() {
    let name = this.customerSearchForm.controls.name?.value;

    if (name?.length < 3) {
      return;
    }

    const filter = {
      filters: {
        name: { value: name },
        addressLine1: {},
        companyRegistrationNumber: {},
        country: {},
        organisationTypeName: { value: '2' },
        postcode: {},
        town: {},
        activeOnly: {},
      },
    };

    this.customerService.getPage(filter).then((result) => {
      this.customers = result.items;
      this.customersLoaded = true;
    });
  }

  processMovementLimits(
    res: DailyMovementLimitResult[],
    type: OrderLimitTypeEnum,
  ): EventSourceInput {
    const result = res.reduce<EventInput[]>((acc, r) => {
      return acc.concat([
        // global total/limit
        {
          start: r.date,
          end: r.date,
          resourceEditable: false,
          display: 'block',
          title:
            type === OrderLimitTypeEnum.Driven
              ? `Global ${r.global.drivenTotal}/${r.global.drivenLimit ?? 'any'}`
              : `Global ${r.global.transportedTotal}/${r.global.transportedLimit ?? 'any'}`,
          description: 'Global Total/Limit',
        },
        // customer total/limit
        (type === OrderLimitTypeEnum.Driven &&
          (r.customer.drivenLimit || r.customer.drivenTotal > 0)) ||
        (type === OrderLimitTypeEnum.Transported &&
          (r.customer.transportedLimit || r.customer.transportedLimit > 0))
          ? {
              start: r.date,
              end: r.date,
              resourceEditable: false,
              display: 'block',
              title:
                type === OrderLimitTypeEnum.Driven
                  ? `Customer ${r.customer.drivenTotal}/${r.customer.drivenLimit ?? 'any'}`
                  : `Customer ${r.customer.transportedTotal}/${
                      r.customer.transportedLimit ?? 'any'
                    }`,
              description: 'Customer Total/Limit',
            }
          : null,
      ]);
    }, []);

    return result.filter((r) => !!r);
  }

  onDateClicked(event: any, type: OrderLimitTypeEnum) {
    if (!this.userIsPOAD) {
      console.log('Only portal administrators can set daily limits');
      return;
    }

    //console.log("Date clicked: ", event, type)
    this.selectedDate = moment(event.date).format('ddd DD MMM YYYY');

    // set form values if we have a customer or global limit on this date
    const eventDate = moment(event.date);
    let ml = this.movementLimits.find((x) => moment(x.date).isSame(eventDate, 'day'));

    if (ml) {
      this.form.patchValue({
        drivenLimit: ml.global.drivenLimit,
        transportedLimit: ml.global.transportedLimit,
        customerDrivenLimit: ml.customer.drivenLimit,
        customerTransportedLimit: ml.customer.transportedLimit,
      });
    }

    this.form.patchValue({
      date: event.date,
      customerId: this.selectedCustomer?.id,
    });

    this.editDayLimits = true;
  }

  onCustomerSelected() {
    //console.log("Customer: ", this.selectedCustomer);
    if (this.currentCustomerId == this.selectedCustomer?.id) return;

    this.dailyLimitsService.getDailyMovementLimits(this.selectedCustomer?.id).then((res) => {
      //console.log("LIMITS: ", res);
      this.movementLimits = res;
      this.resetLimits();
      this.currentCustomerId = this.selectedCustomer?.id;
    });
  }

  saveDayLimit() {
    this.dailyLimitsService.save(this.form.value).then(() => {
      this.editDayLimits = false;

      this.dailyLimitsService.getDailyMovementLimits(this.selectedCustomer?.id).then((res) => {
        this.movementLimits = res;
        this.resetLimits();
      });
    });
  }

  closeDialog() {
    this.editDayLimits = false;
  }
}
